import {Stack, Typography} from '@mui/material';
import React from 'react';

import {OdontoBookingTaggingEnum} from 'main/analytics/gaTagsEnum';
import {SummaryCard, SummaryCardSection} from 'presentation/components';
import {translations} from 'presentation/translations-new';
import {formatMasks, formatText} from 'presentation/utils';

import {BookingTypeEnum} from 'presentation/modules/booking/shared/constants/bookingTypeEnum';
import {FlowEnum} from 'presentation/modules/booking/shared/constants/flowEnum';
import {AppointmenInstructionsAlertInfo} from 'presentation/modules/booking/shared/ui/components/AppointmenInstructionsAlertInfo';
import {ChangeFlowVariantEnum} from 'presentation/modules/booking/shared/ui/components/ChangeFlowDialog/constants';
import {ReviewCard} from 'presentation/modules/booking/shared/ui/components/ReviewCard';
import {SummaryDistrictSection} from 'presentation/modules/booking/shared/ui/components/SummaryDistrictSection';
import {useOdontoSummarySectionGroup} from './hooks/useOdontoSummarySectionGroup';
import {OdontoSummarySectionGroupProps} from './types';
import {OdontoRecommendations} from '../OdontoRecommendations';
import {formatOdontoRecommendations} from '../../../utils/formatOdontoRecommendations';

const {appointmentCity} = translations['pt-br'].privateFlow.profile;
const {appointmentSummary, beneficiaryData, cardCode} =
	translations['pt-br'].privateFlow.bookingFlow;
const {attendanceUnit, specialty, contactInfo, beneficiaryPlan, observations} =
	translations['pt-br'].commons;
const {appointmentInstructions} =
	translations['pt-br'].privateFlow.myAppointments.myAppointmentDetails;

export function OdontoSummarySectionGroup({
	stepLabels,
	activeStep,
	handleChangeModals,
	disableGutters,
	showTitle,
	noBorderRadius,
}: OdontoSummarySectionGroupProps) {
	const {
		showSpecialtyField,
		showUnitField,
		getLocationPrimaryText,
		getLocationSecondaryText,
		showDistricsField,
		bookingTheme,
		colors,
		cdUsuario,
		beneficiaryCardPlanData,
		currentEmail,
		currentPhone,
		isLastStep,
		isUsingGeolocation,
		isRebooking,
		unitName,
		unitAddress,
		specialtyCode,
		specialtyName,
		handleChangeLocation,
		odontoRecommendations,
		showOdontoRecommendations,
	} = useOdontoSummarySectionGroup({
		activeStep,
		stepLabels,
		handleChangeModals,
	});

	if (!isLastStep) {
		return (
			<SummaryCard
				noBorderRadius={noBorderRadius}
				showTitle={showTitle}
				disableGuters={disableGutters}
				title={appointmentSummary}
				hideAction>
				<SummaryCardSection
					hideAction={true}
					disableGutters={disableGutters}
					hasDivider
					subheader={contactInfo}
					primaryText={formatMasks('phone', currentPhone)}
					iconName="phone-android-filled"
					disableSecondaryTextFormat
					secondaryData={{
						primaryText: currentEmail,
						iconName: 'mail-filled',
					}}
				/>
				{showSpecialtyField ? (
					<SummaryCardSection
						hideAction={isRebooking}
						handleAction={() => handleChangeModals(ChangeFlowVariantEnum.SPECIALTY)}
						disableGutters={disableGutters}
						hasDivider
						subheader={specialty}
						primaryText={specialtyName}
						iconName={bookingTheme.iconName}
						dataTagId={`${OdontoBookingTaggingEnum.CLICK_ALTERAR_ESPECIALIDADE}${FlowEnum.ODONTO}`}
					/>
				) : null}

				<SummaryCardSection
					dataTagId={`${OdontoBookingTaggingEnum.CLICK_ALTERAR_LOCAL_DE_ATENDIMENTO}${FlowEnum.ODONTO}`}
					hideAction={isRebooking}
					handleAction={handleChangeLocation}
					disableGutters={disableGutters}
					hasDivider
					subheader={appointmentCity}
					primaryText={getLocationPrimaryText()}
					disablePrimaryTextFormat={isUsingGeolocation}
					secondaryText={getLocationSecondaryText()}
					iconName="my-location-filled"
				/>

				{showDistricsField ? (
					<SummaryDistrictSection
						flowType={FlowEnum.ODONTO}
						dataTagId={`${OdontoBookingTaggingEnum.CLICK_ALTERAR_BAIRRO}${FlowEnum.ODONTO}`}
					/>
				) : null}

				{showUnitField ? (
					<SummaryCardSection
						hideAction={isRebooking}
						handleAction={() => handleChangeModals(ChangeFlowVariantEnum.UNIT)}
						disableGutters={disableGutters}
						hasDivider
						subheader={attendanceUnit}
						primaryText={unitName}
						iconName={'location-on-filled'}
						secondaryText={unitAddress}
					/>
				) : null}

				{showOdontoRecommendations ? (
					<OdontoRecommendations
						subheader={observations}
						hasDivider
						recommendations={formatOdontoRecommendations([odontoRecommendations!])}
					/>
				) : null}
			</SummaryCard>
		);
	}

	return (
		<Stack gap={2}>
			<Typography variant="subtitle2" color={colors.light.text.secondary}>
				{beneficiaryData}
			</Typography>
			<ReviewCard.Root>
				<ReviewCard.Header subheader={beneficiaryPlan} />
				<Stack gap={'16px'}>
					<ReviewCard.Content
						iconName={'account-circle-filled'}
						primaryText={formatText(beneficiaryCardPlanData.userName)}
					/>
					<ReviewCard.Content
						iconName={'hapvida-logo'}
						primaryText={formatText(beneficiaryCardPlanData.planName)}
						secondaryText={`${cardCode}${cdUsuario}`}
					/>
				</Stack>
			</ReviewCard.Root>
			<ReviewCard.Root>
				<ReviewCard.Header subheader={contactInfo} />
				<Stack gap={'8px'}>
					<ReviewCard.Content
						iconName={'phone-android-filled'}
						primaryText={formatMasks('phone', currentPhone)}
					/>
					<ReviewCard.Content
						iconName={'mail-filled'}
						primaryText={currentEmail}
						disablePrimaryTextFormat
					/>
				</Stack>
			</ReviewCard.Root>
			<Stack gap={2}>
				<Typography variant="subtitle2" color={colors.light.text.secondary}>
					{appointmentInstructions.title}
				</Typography>
				<AppointmenInstructionsAlertInfo
					bookingType={BookingTypeEnum.ODONTO}
					specialtyCode={specialtyCode}
					specialtyName={specialtyName}
				/>
			</Stack>
		</Stack>
	);
}
